/**
 * Hot/cold: the pulsing proximity ring (`ProductOverview.md` §4.5).
 *
 * Below thirty metres a bearing is not trustworthy, so the arrow morphs into this: a ring that
 * pulses faster as the gap closes, with matching haptics, under a caption that says plainly why
 * the arrow went away. Being honest about the limit is the whole point of the mode.
 *
 * DOM and CSS rather than WebGL — this is a circle and a beat, and it should cost nothing.
 */

import { proximityHeat, pulseIntervalMs, RANGE } from '@beeline/shared';

import { el } from '../lib/dom';
import { haptics } from '../lib/haptics';

export interface ProximityView {
  element: HTMLElement;
  update: (distanceMetres: number | null, options?: { haptic?: boolean }) => void;
  destroy: () => void;
}

export function createProximityView(): ProximityView {
  const ring = el('div', { class: 'proximity__ring' });
  const echo = el('div', { class: 'proximity__echo' });
  const core = el('div', { class: 'proximity__core' });

  const caption = el('p', {
    class: 'proximity__caption',
    text: `GPS is fuzzy at this range — you're close.`,
  });

  const element = el(
    'div',
    { class: 'proximity' },
    el('div', { class: 'proximity__stack' }, ring, echo, core),
    caption,
  );

  let timer: number | null = null;
  let interval = pulseIntervalMs(RANGE.hotColdMetres);
  let heat = 0;
  let hapticsOn = true;

  const beat = (): void => {
    // Restart the animation by taking the class off and putting it back on the next frame.
    echo.classList.remove('proximity__echo--beat');
    void echo.offsetWidth;
    echo.classList.add('proximity__echo--beat');

    if (hapticsOn) haptics.pulse(heat);

    timer = window.setTimeout(beat, interval);
  };

  const start = (): void => {
    if (timer !== null) return;
    beat();
  };

  const stop = (): void => {
    if (timer === null) return;
    window.clearTimeout(timer);
    timer = null;
  };

  // No point beating away at a screen nobody is looking at, and definitely no buzzing.
  const onVisibility = (): void => {
    if (document.visibilityState === 'hidden') stop();
    else start();
  };
  document.addEventListener('visibilitychange', onVisibility);

  start();

  return {
    element,
    update: (distanceMetres, options = {}) => {
      hapticsOn = options.haptic ?? true;
      heat = proximityHeat(distanceMetres);
      interval = pulseIntervalMs(distanceMetres);

      // The ring tightens as you close in, so the beat is not the only signal.
      const scale = 0.55 + (1 - heat) * 0.45;
      element.style.setProperty('--proximity-scale', scale.toFixed(3));
      element.style.setProperty('--proximity-beat', `${interval}ms`);
      element.style.setProperty('--proximity-heat', heat.toFixed(3));
    },
    destroy: () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      haptics.stop();
      element.remove();
    },
  };
}
